// =============================================================================
// BP-08-05: Vendor Portal Frontend - AnalyticsCharts Component
// =============================================================================

import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import type { ViewsData, InquiriesData, RevenueData, TopProduct } from '@/features/vendor/types/vendorTypes';

// Chart colors
const COLORS = {
  primary: '#C4785A', // terracotta
  secondary: '#7A9E7E', // sage
  tertiary: '#2B3A4D', // navy
  light: '#D99B7E', // terracotta-light
  muted: '#8B8178', // warm-gray
};

const RATING_COLORS = ['#10B981', '#22C55E', '#EAB308', '#F97316', '#EF4444'];

interface ChartContainerProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function ChartContainer({ title, subtitle, children }: ChartContainerProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="font-display text-lg font-semibold text-navy">{title}</h3>
        {subtitle && <p className="text-sm text-warm-gray mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// Views Chart
interface ViewsChartProps {
  data: ViewsData[];
}

export function ViewsChart({ data }: ViewsChartProps) {
  return (
    <ChartContainer title="Product Views" subtitle="Total and unique views over time">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.muted }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis tick={{ fontSize: 12, fill: COLORS.muted }} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#FDF8F3',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
            }}
            labelFormatter={(value) => new Date(value).toLocaleDateString()}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="views"
            name="Total Views"
            stroke={COLORS.primary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="uniqueViews"
            name="Unique Views"
            stroke={COLORS.secondary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

// Inquiries Chart
interface InquiriesChartProps {
  data: InquiriesData[];
}

export function InquiriesChart({ data }: InquiriesChartProps) {
  return (
    <ChartContainer title="Inquiries & Quote Requests" subtitle="Customer interest over time">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.muted }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis tick={{ fontSize: 12, fill: COLORS.muted }} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#FDF8F3',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
            }}
            labelFormatter={(value) => new Date(value).toLocaleDateString()}
          />
          <Legend />
          <Bar
            dataKey="inquiries"
            name="Inquiries"
            fill={COLORS.primary}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="quoteRequests"
            name="Quote Requests"
            fill={COLORS.secondary}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

// Revenue Chart
interface RevenueChartProps {
  data: RevenueData[];
}

export function RevenueChart({ data }: RevenueChartProps) {
  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value}`;
  };

  return (
    <ChartContainer title="Revenue" subtitle="Revenue and order count over time">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.muted }}
            tickFormatter={(value) => {
              const date = new Date(value);
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12, fill: COLORS.muted }}
            tickFormatter={formatCurrency}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: COLORS.muted }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#FDF8F3',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
            }}
            labelFormatter={(value) => new Date(value).toLocaleDateString()}
            formatter={(value: number, name: string) => {
              if (name === 'Revenue') return formatCurrency(value);
              return value;
            }}
          />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="revenue"
            name="Revenue"
            stroke={COLORS.primary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="orderCount"
            name="Orders"
            stroke={COLORS.tertiary}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

// Rating Distribution Chart
interface RatingDistributionChartProps {
  distribution: Record<number, number>;
}

export function RatingDistributionChart({ distribution }: RatingDistributionChartProps) {
  const data = [5, 4, 3, 2, 1].map((rating) => ({
    rating: `${rating} Star`,
    count: distribution[rating] || 0,
  }));

  const total = Object.values(distribution).reduce((a, b) => a + b, 0);

  return (
    <ChartContainer title="Rating Distribution" subtitle={`${total} total reviews`}>
      <div className="space-y-3">
        {data.map((item, index) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;
          return (
            <div key={item.rating} className="flex items-center gap-3">
              <span className="text-sm font-medium text-navy w-16">{item.rating}</span>
              <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: RATING_COLORS[index],
                  }}
                />
              </div>
              <span className="text-sm text-warm-gray w-16 text-right">
                {item.count} ({percentage.toFixed(0)}%)
              </span>
            </div>
          );
        })}
      </div>
    </ChartContainer>
  );
}

// Top Products Chart
interface TopProductsChartProps {
  products: TopProduct[];
}

export function TopProductsChart({ products }: TopProductsChartProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <ChartContainer title="Top Products" subtitle="By views and revenue">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-semibold text-warm-gray uppercase tracking-wide">
              <th className="pb-3">Product</th>
              <th className="pb-3 text-right">Views</th>
              <th className="pb-3 text-right">Inquiries</th>
              <th className="pb-3 text-right">Orders</th>
              <th className="pb-3 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {products.map((product, index) => (
              <tr key={product.productId} className="text-sm">
                <td className="py-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-terracotta/10 text-terracotta text-xs font-semibold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="font-medium text-navy">{product.productName}</span>
                  </div>
                </td>
                <td className="py-3 text-right text-navy">{product.views.toLocaleString()}</td>
                <td className="py-3 text-right text-navy">{product.inquiries}</td>
                <td className="py-3 text-right text-navy">{product.orders}</td>
                <td className="py-3 text-right font-semibold text-navy">
                  {formatCurrency(product.revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartContainer>
  );
}

export default {
  ViewsChart,
  InquiriesChart,
  RevenueChart,
  RatingDistributionChart,
  TopProductsChart,
};

